import React from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@material-ui/core";
import { Field, Form } from "react-final-form";
import createDecorator from "final-form-focus";
import { string, object, ref } from "yup";
import { useYupValidation } from "../../hooks";
import { Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";

const focusOnErrors = createDecorator();

const VACCINE_BRANDS = [
  {
    id: "az",
    label: "AstraZeneca"
  },
  { id: "janssen", label: "Johnson & Johnson's Janssen", doses: [1] },
  {
    id: "moderna",
    label: "Moderna"
  },
  {
    id: "novavax",
    label: "Novavax"
  },
  { id: "pfizer", label: "Pfizer" }
];

const validationSchema = object({
  vax_type: string()
    .required()
    .label("Vaccine brand"),
  vax_1_lot_number: string()
    .trim()
    .required()
    .label("Lot Number (Dose 1)"),
  vax_1_date: string()
    .required()
    .label("Vaccination Date (Dose 1)"),
  vax_2_lot_number: string().when("vax_type", {
    is: val =>
      val === "pfizer" ||
      val === "moderna" ||
      val === "novavax" ||
      val === "az",
    then: string()
      .required()
      .label("Lot Number (Dose 2)")
  }),
  vax_2_date: string().when("vax_type", {
    is: val =>
      val === "pfizer" ||
      val === "moderna" ||
      val === "novavax" ||
      val === "az",
    then: string()
      .test(
        "After dose 1 date",
        "Dose 2 vacination date must be after Dose 1",
        function(value) {
          return new Date(value) > new Date(this.resolve(ref("vax_1_date")));
        }
      )
      .label("Vaccination Date (Dose 2)")
  })
});

const getChosenBrandDoses = (chosenBrandId, brands) => {
  const DEFAULT_DOSES = [1, 2];

  const [brand] = brands.filter(brand => brand.id === chosenBrandId);

  return brand && brand.doses ? brand.doses : DEFAULT_DOSES;
};

const VaccineTypeRegisterForm = ({
  children,
  initialValues = {},
  onSubmitSuccess = () => {}
}) => {
  const { t } = useTranslation();
  const { validate } = useYupValidation();

  const onSubmit = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    onSubmitSuccess(data);
  };

  /**
   * Ensure date/lot fields are reset appropriately when changing brands
   * since not all brands have two doses
   *
   * @param {string} brand
   */
  const onBrandChange = (brand, form) => {
    const dateToday = moment().format("YYYY-MM-DD");

    const additionalValues =
      brand !== "janssen" ? { vax_2_date: dateToday } : { vax_2_date: "" };

    form.reset({
      vax_1_lot_number: "",
      vax_1_date: dateToday,
      vax_2_lot_number: "",
      ...additionalValues
    });
  };

  return (
    <Form
      onSubmit={onSubmit}
      decorators={[focusOnErrors]}
      initialValues={initialValues}
      render={({ handleSubmit, submitErrors: errors = {}, values, form }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} direction="column">
            <Grid item xs>
              <Field
                name="vax_type"
                render={({ input }) => (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="vaccine-type-brand">
                      {t(
                        "components.vax_yes.vaccine_type_register_form.brand_input_label"
                      )}
                    </InputLabel>
                    <Select
                      {...input}
                      onChange={e => {
                        onBrandChange(e.target.value, form);
                        input.onChange(e.target.value);
                      }}
                      native
                      labelId="vaccine-type-brand"
                      label={t(
                        "components.vax_yes.vaccine_type_register_form.brand_label"
                      )}
                      fullWidth
                      inputProps={{ "data-cy": "input-brand" }}
                    >
                      {[
                        {
                          value: "",
                          label: t(
                            "components.vax_yes.vaccine_type_register_form.brand_label_none"
                          )
                        },
                        ...VACCINE_BRANDS
                      ].map(brand => (
                        <option key={`brand-${brand.id}`} value={brand.id}>
                          {brand.label}
                        </option>
                      ))}
                    </Select>
                    {!!errors.vax_type && (
                      <FormHelperText error={true}>
                        {errors.vax_type}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            {values.vax_type && (
              <Grid item xs>
                <Typography align="center" paragraph>
                  {t("components.vax_yes.vaccine_type_register_form.dose_text")}
                </Typography>
                <Grid container spacing={2} direction="column">
                  {getChosenBrandDoses(values.vax_type, VACCINE_BRANDS).map(
                    dose => (
                      <React.Fragment key={dose}>
                        <Grid item xs>
                          <Typography component="h2" variant="h6">
                            <strong>
                              {t(
                                "components.vax_yes.vaccine_type_register_form.dose_title",
                                {
                                  dose: dose
                                }
                              )}
                            </strong>
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Field
                            name={`vax_${dose}_lot_number`}
                            render={({ input }) => (
                              <TextField
                                {...input}
                                label={t(
                                  "components.vax_yes.vaccine_type_register_form.dose_label",
                                  {
                                    dose: dose
                                  }
                                )}
                                fullWidth
                                margin="none"
                                variant="outlined"
                                error={!!errors[`vax_${dose}_lot_number`]}
                                helperText={errors[`vax_${dose}_lot_number`]}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs>
                          <Field
                            name={`vax_${dose}_date`}
                            format={val => moment(val).format()}
                            parse={val => moment(val).format("YYYY-MM-DD")}
                            render={({ input }) => (
                              <DatePicker
                                inputVariant="outlined"
                                label={t(
                                  "components.vax_yes.vaccine_type_register_form.vaccination_date_label",
                                  {
                                    dose: dose
                                  }
                                )}
                                format="MM/DD/yyyy"
                                margin="normal"
                                {...input}
                                fullWidth
                                disableFuture
                                minDate={new Date(2020, 0, 1)}
                                error={!!errors[`vax_${dose}_date`]}
                                helperText={errors[`vax_${dose}_date`]}
                              />
                            )}
                          />
                        </Grid>
                      </React.Fragment>
                    )
                  )}
                </Grid>
              </Grid>
            )}
            {children}
          </Grid>
        </form>
      )}
    />
  );
};

export { VaccineTypeRegisterForm };
